import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Phone, PhoneCall, ChatCircleDots, CalendarCheck, CheckCircle, Receipt,
  Archive, Trash, UploadSimple, FilePdf, MapPin, MagnifyingGlass, Plus,
  ClockCounterClockwise, EnvelopeSimple, X, ArrowClockwise, ArrowsClockwise,
  Copy, GearSix,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const STATUSES = [
  { key: "a_appeler", label: "À appeler", cls: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  { key: "message_laisse", label: "Message laissé", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  { key: "rdv_fixe", label: "RDV fixé", cls: "bg-violet-100 text-violet-700", dot: "bg-violet-500" },
  { key: "visite_faite", label: "Visite faite", cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  { key: "facture", label: "Facturé", cls: "bg-teal-100 text-teal-700", dot: "bg-teal-500" },
  { key: "cloture", label: "Clôturé", cls: "bg-slate-200 text-slate-600", dot: "bg-slate-400" },
];
const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" }) : "");

const StatusBadge = ({ status }) => {
  const m = statusMeta(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
};

export default function Visits() {
  const [visits, setVisits] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [calInfo, setCalInfo] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (tab !== "all") params.status = tab;
      if (search.trim()) params.search = search.trim();
      const [v, c, cal] = await Promise.all([
        api.get("/visits", { params }),
        api.get("/visits/counts"),
        api.get("/settings/calendar"),
      ]);
      setVisits(v.data);
      setCounts(c.data);
      setCalInfo(cal.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    try {
      const res = await api.get("/visits/export/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "evolution-demandes.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossible de générer le PDF");
    }
  };

  const exportInvoicePdf = async () => {
    try {
      const res = await api.get("/visits/export/invoice-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "facturation-controles-chaudieres.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossible de générer le PDF de facturation");
    }
  };

  const addManual = async () => {
    const name = window.prompt("Nom du client ?");
    if (!name) return;
    try {
      const { data } = await api.post("/visits", { client_name: name });
      toast.success("Client ajouté");
      await load();
      setSelected(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const syncCalendar = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/visits/sync-calendar");
      toast.success(`Agenda synchronisé — ${data.updated} RDV mis à jour (${data.matched} trouvé(s))`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Configurez d'abord le lien iCal");
      if (err.response?.status === 400) setCalOpen(true);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-64 p-8" data-testid="visits-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="label-eyebrow mb-1">Prospection</p>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-950">Visites à planifier</h1>
            <p className="text-sm text-slate-500 mt-1">Votre liste vivante : appelez, laissez un message, fixez le RDV — tout est tracé.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="visits-sync-calendar" onClick={syncCalendar} disabled={syncing} className="h-10">
              <ArrowsClockwise size={16} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchro…" : "Synchroniser l'agenda"}
            </Button>
            <Button variant="outline" size="icon" data-testid="visits-calendar-settings" onClick={() => setCalOpen(true)} className="h-10 w-10" title="Réglage du lien Google Agenda">
              <GearSix size={16} />
            </Button>
            <Button variant="outline" data-testid="visits-add-manual" onClick={addManual} className="h-10">
              <Plus size={16} className="mr-1.5" /> Ajouter
            </Button>
            <Button variant="outline" data-testid="visits-export-pdf" onClick={exportPdf} className="h-10">
              <FilePdf size={16} className="mr-1.5" /> Exporter (PDF)
            </Button>
            <Button variant="outline" data-testid="visits-export-invoice-pdf" onClick={exportInvoicePdf} className="h-10">
              <Receipt size={16} className="mr-1.5" /> PDF facturation
            </Button>
            <Button data-testid="visits-import-open" onClick={() => setImportOpen(true)} className="h-10 bg-blue-600 hover:bg-blue-700">
              <UploadSimple size={16} className="mr-1.5" /> Importer une liste
            </Button>
          </div>
        </div>

        {/* Sync status badge */}
        {calInfo && (
          <div className="mb-4">
            {calInfo.ical_url ? (
              <span data-testid="sync-badge" className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded-full">
                <CalendarCheck size={13} /> Google Agenda relié
                {calInfo.last_sync_at
                  ? ` · synchronisé le ${new Date(calInfo.last_sync_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}`
                  : " · pas encore synchronisé"}
                {calInfo.last_sync_result ? ` · ${calInfo.last_sync_result.matched} RDV relié(s)` : ""}
              </span>
            ) : (
              <button data-testid="sync-badge-setup" onClick={() => setCalOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-100 px-2.5 py-1 rounded-full hover:bg-amber-100 transition-colors">
                <GearSix size={13} /> Google Agenda non relié — cliquez pour configurer
              </button>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <button data-testid="visits-tab-all" onClick={() => setTab("all")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "all" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            Tous <span className="opacity-70">({counts.total || 0})</span>
          </button>
          {STATUSES.map((s) => (
            <button key={s.key} data-testid={`visits-tab-${s.key}`} onClick={() => setTab(s.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === s.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
              {s.label} <span className="opacity-70">({counts[s.key] || 0})</span>
            </button>
          ))}
        </div>

        <div className="relative mb-4 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="visits-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client, une adresse, un numéro…" className="pl-9 h-10" />
        </div>

        {/* List */}
        {loading ? (
          <p className="text-slate-400">Chargement…</p>
        ) : visits.length === 0 ? (
          <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-md p-10 text-center">
            Aucun client dans cette liste. Importez votre liste hebdomadaire (PDF / Excel) pour commencer. 📋
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="visits-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Contact</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium">RDV</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v) => (
                  <tr key={v.id} data-testid={`visit-row-${v.id}`} onClick={() => setSelected(v)}
                    className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{v.client_name || "—"}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin size={12} /> {v.address || "Adresse non renseignée"}
                      </p>
                      {v.code && <span className="inline-block mt-1 text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{v.code}</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {v.phone && <p className="flex items-center gap-1"><Phone size={12} /> {v.phone}</p>}
                      {v.email && <p className="text-xs text-slate-400 truncate max-w-[180px]">{v.email}</p>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {v.rdv_at ? fmtDate(v.rdv_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selected && (
        <VisitDetail visit={selected} onClose={() => setSelected(null)} onChanged={(v) => { setSelected(v); load(); }} />
      )}
      {importOpen && (
        <ImportDialog onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); load(); }} />
      )}
      {calOpen && <CalendarSettings onClose={() => setCalOpen(false)} />}
    </div>
  );
}

/* ---------------- Detail drawer ---------------- */
function VisitDetail({ visit, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [rdvOpen, setRdvOpen] = useState(false);
  const [rdvAt, setRdvAt] = useState("");
  const [rdvDur, setRdvDur] = useState(60);
  const [rdvAddr, setRdvAddr] = useState(visit.rdv_address || visit.site_address || visit.address || "");

  const act = async (type, noteText = "") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/visits/${visit.id}/action`, { type, note: noteText });
      toast.success("Enregistré");
      onChanged(data);
      setNote("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/visits/${visit.id}/status`, { status, ...extra });
      toast.success("Statut mis à jour");
      onChanged(data);
      setRdvOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const confirmRdv = () => {
    if (!rdvAt || !rdvDur || !rdvAddr.trim()) {
      toast.error("Date/heure, durée et adresse sont obligatoires.");
      return;
    }
    setStatus("rdv_fixe", { rdv_at: new Date(rdvAt).toISOString(), rdv_duration_minutes: Number(rdvDur), rdv_address: rdvAddr.trim() });
  };

  const archive = async () => {
    setBusy(true);
    try {
      await api.post(`/visits/${visit.id}/archive?archived=true`);
      toast.success("Archivé");
      onClose(); onChanged(visit);
    } catch (err) { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette fiche ?")) return;
    try {
      await api.delete(`/visits/${visit.id}`);
      toast.success("Supprimé");
      onClose(); onChanged(visit);
    } catch (err) { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="visit-detail">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="font-heading text-xl font-medium text-slate-950">{visit.client_name || "Client"}</h2>
            <div className="mt-1"><StatusBadge status={visit.status} /></div>
          </div>
          <button data-testid="visit-detail-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Coordonnées */}
          <div className="space-y-1.5 text-sm">
            {visit.address && <p className="flex items-start gap-2 text-slate-700"><MapPin size={15} className="mt-0.5 text-slate-400" /> {visit.address}</p>}
            {visit.site_address && <p className="flex items-start gap-2 text-slate-700"><MapPin size={15} className="mt-0.5 text-blue-500" /> Chantier : {visit.site_address}</p>}
            {visit.phone && <p className="flex items-center gap-2 text-slate-700"><Phone size={15} className="text-slate-400" /> <a href={`tel:${visit.phone}`} className="text-blue-600 hover:underline">{visit.phone}</a></p>}
            {visit.email && <p className="flex items-center gap-2 text-slate-700"><EnvelopeSimple size={15} className="text-slate-400" /> {visit.email}</p>}
          </div>

          {/* Code agenda */}
          <div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Code agenda</p>
                <p className="font-mono text-lg font-semibold text-slate-900" data-testid="visit-code">{visit.code || "—"}</p>
              </div>
              <Button variant="outline" size="sm" data-testid="visit-copy-code"
                onClick={() => { navigator.clipboard?.writeText(visit.code || ""); toast.success("Code copié"); }}>
                <Copy size={14} className="mr-1.5" /> Copier
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Ajoutez ce code dans votre événement Google Agenda → le RDV se reliera tout seul à cette fiche.</p>
          </div>

          {/* Actions rapides */}
          <div>
            <p className="label-eyebrow mb-2">Actions rapides</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" data-testid="visit-act-appel" disabled={busy} onClick={() => act("appel")}><PhoneCall size={15} className="mr-1.5" /> Appel effectué</Button>
              <Button variant="outline" size="sm" data-testid="visit-act-message" disabled={busy} onClick={() => act("message")}><ChatCircleDots size={15} className="mr-1.5" /> Message laissé</Button>
              <Button variant="outline" size="sm" data-testid="visit-act-rappel" disabled={busy} onClick={() => act("rappel")}><ArrowClockwise size={15} className="mr-1.5" /> Rappelé</Button>
            </div>
            <div className="flex gap-2 mt-2">
              <Input data-testid="visit-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note…" className="h-9" />
              <Button variant="outline" size="sm" data-testid="visit-act-note" disabled={busy || !note.trim()} onClick={() => act("note", note.trim())}>OK</Button>
            </div>
          </div>

          {/* Progression du statut */}
          <div>
            <p className="label-eyebrow mb-2">Faire avancer</p>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" data-testid="visit-status-rdv" disabled={busy} onClick={() => setRdvOpen(true)} className="bg-violet-600 hover:bg-violet-700"><CalendarCheck size={15} className="mr-1.5" /> Fixer un RDV</Button>
              <Button size="sm" variant="outline" data-testid="visit-status-visite" disabled={busy} onClick={() => setStatus("visite_faite")}><CheckCircle size={15} className="mr-1.5" /> Visite faite</Button>
              <Button size="sm" variant="outline" data-testid="visit-status-facture" disabled={busy} onClick={() => setStatus("facture")}><Receipt size={15} className="mr-1.5" /> Facturé</Button>
              <Button size="sm" variant="outline" data-testid="visit-status-cloture" disabled={busy} onClick={() => setStatus("cloture")}><CheckCircle size={15} className="mr-1.5" /> Clôturer</Button>
            </div>
            {visit.rdv_at && (
              <p className="text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded px-2.5 py-1.5 mt-2">
                RDV : {fmtDate(visit.rdv_at)}{visit.rdv_duration_minutes ? ` (${visit.rdv_duration_minutes} min)` : ""}{visit.rdv_address ? ` — ${visit.rdv_address}` : ""}
              </p>
            )}
            {visit.calendar_note && (
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded px-2.5 py-1.5 mt-2 whitespace-pre-wrap" data-testid="visit-calendar-note">
                📝 {visit.calendar_note}
              </p>
            )}
          </div>

          {/* Historique */}
          <div>
            <p className="label-eyebrow mb-2 flex items-center gap-1.5"><ClockCounterClockwise size={14} /> Historique</p>
            <div className="space-y-2" data-testid="visit-history">
              {(visit.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 shrink-0" />
                  <div>
                    <p className="text-slate-700">{h.label}{h.note ? ` — ${h.note}` : ""}</p>
                    <p className="text-xs text-slate-400">{fmtDate(h.at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <button data-testid="visit-archive" onClick={archive} disabled={busy} className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-1"><Archive size={14} /> Archiver</button>
            <button data-testid="visit-delete" onClick={remove} className="text-xs text-slate-400 hover:text-red-600 flex items-center gap-1"><Trash size={14} /> Supprimer</button>
          </div>
        </div>
      </div>

      {/* RDV modal */}
      <Dialog open={rdvOpen} onOpenChange={setRdvOpen}>
        <DialogContent data-testid="rdv-dialog">
          <DialogHeader><DialogTitle>Fixer un rendez-vous</DialogTitle>
            <DialogDescription>Renseignez la date, la durée et l'adresse de la visite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-eyebrow">Date et heure</label>
              <Input data-testid="rdv-datetime" type="datetime-local" value={rdvAt} onChange={(e) => setRdvAt(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="label-eyebrow">Durée (minutes)</label>
              <Input data-testid="rdv-duration" type="number" min="15" step="15" value={rdvDur} onChange={(e) => setRdvDur(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="label-eyebrow">Adresse de la visite</label>
              <Input data-testid="rdv-address" value={rdvAddr} onChange={(e) => setRdvAddr(e.target.value)} className="mt-1" />
            </div>
            <p className="text-xs text-slate-400">Ces 3 champs sont obligatoires. (La synchro Google Agenda sera branchée en Phase 2.)</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRdvOpen(false)}>Annuler</Button>
            <Button data-testid="rdv-confirm" onClick={confirmRdv} disabled={busy} className="bg-violet-600 hover:bg-violet-700">Confirmer le RDV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Import dialog ---------------- */
function ImportDialog({ onClose, onImported }) {
  const fileRef = useRef();
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [gmail, setGmail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [dupCount, setDupCount] = useState(0);

  const loadGmail = useCallback(async () => {
    try { const { data } = await api.get("/gmail/status"); setGmail(data); } catch (e) {}
  }, []);
  useEffect(() => { loadGmail(); }, [loadGmail]);

  const connectGmail = async () => {
    try {
      const { data } = await api.get("/gmail/auth-url");
      window.open(data.url, "_blank", "width=520,height=680");
      toast.info("Autorisez l'accès Gmail dans la fenêtre, puis revenez ici et cliquez « Actualiser ».");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const importFromGmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gmail/import-now");
      toast.success(`Gmail : ${data.imported} client(s) importé(s) depuis ${data.new} email(s)`);
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const disconnectGmail = async () => {
    try { await api.post("/gmail/disconnect"); toast.success("Gmail déconnecté"); loadGmail(); } catch (e) {}
  };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/visits/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(data.rows);
      setSelected(data.rows.map((r) => !r.duplicate));
      setDupCount(data.duplicates || 0);
      setSource(data.source || file.name);
      setLabel(`Liste ${file.name.replace(/\.[^.]+$/, "")}`);
      if (data.rows.length === 0) toast.error("Aucun client détecté dans ce fichier.");
      else if (data.duplicates) toast.success(`${data.rows.length} détecté(s) · ${data.duplicates} doublon(s) décoché(s)`);
      else toast.success(`${data.rows.length} client(s) détecté(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    const chosen = rows.filter((_, i) => selected[i]);
    if (chosen.length === 0) { toast.error("Aucun client sélectionné."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/visits/import/confirm", { rows: chosen, source_label: label });
      toast.success(`${data.inserted} client(s) importé(s)`);
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleRow = (i) => setSelected((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  const selectedCount = selected.filter(Boolean).length;

  const updateCell = (i, key, val) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Importer une liste de clients</DialogTitle>
          <DialogDescription>Depuis un PDF (lecture automatique par IA), un fichier Excel ou CSV.</DialogDescription>
        </DialogHeader>
        {!rows ? (
          <div>
            {gmail && (
              <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="gmail-panel">
                <div className="flex items-center gap-2 mb-1">
                  <EnvelopeSimple size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-slate-800">Import automatique depuis Gmail</span>
                </div>
                {!gmail.configured ? (
                  <p className="text-xs text-slate-400">Bientôt disponible — identifiants Google à configurer (voir le guide envoyé).</p>
                ) : gmail.connected ? (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Connecté : <strong>{gmail.email}</strong> · Expéditeur suivi : <strong>{gmail.sender}</strong>
                      {gmail.last_import_at ? ` · Dernier import : ${new Date(gmail.last_import_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}` : ""}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" data-testid="gmail-import-now" onClick={importFromGmail} disabled={busy} className="bg-blue-600 hover:bg-blue-700">
                        <UploadSimple size={14} className="mr-1.5" /> Importer depuis Gmail maintenant
                      </Button>
                      <button onClick={disconnectGmail} className="text-xs text-slate-400 hover:text-red-600">Déconnecter</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Connectez votre Gmail (lecture seule) pour importer automatiquement les listes de {gmail.sender}.</p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" data-testid="gmail-connect" onClick={connectGmail}>Connecter Gmail</Button>
                      <button onClick={loadGmail} className="text-xs text-blue-600 hover:underline">Actualiser</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-md p-10 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 transition-colors"
              data-testid="import-dropzone">
              <UploadSimple size={30} className="mx-auto text-blue-500 mb-2" />
              <p className="text-slate-700 font-medium">Cliquez pour choisir un fichier</p>
              <p className="text-xs text-slate-400 mt-1">PDF (lecture automatique par IA) · Excel (.xlsx) · CSV</p>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv" className="hidden"
              data-testid="import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
            {busy && <p className="text-sm text-blue-600 mt-3 text-center">Lecture du fichier en cours…</p>}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nom de cette liste" className="h-9" data-testid="import-label" />
              <span className="text-xs text-slate-400 whitespace-nowrap">{rows.length} détecté(s){dupCount ? ` · ${dupCount} doublon(s)` : ""}</span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2">Client</th><th className="px-2 py-2">Adresse</th>
                    <th className="px-2 py-2">Tél</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${r.duplicate ? "bg-amber-50/50" : ""}`} data-testid={`import-row-${i}`}>
                      <td className="px-2 py-1 text-center">
                        <input type="checkbox" data-testid={`import-check-${i}`} checked={!!selected[i]} onChange={() => toggleRow(i)} className="cursor-pointer" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={r.client_name} onChange={(e) => updateCell(i, "client_name", e.target.value)} className="w-full px-1.5 py-1 border border-transparent hover:border-slate-200 rounded" />
                        {r.duplicate && <span className="ml-1 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">déjà présent{r.duplicate_info ? ` (${r.duplicate_info})` : ""}</span>}
                      </td>
                      <td className="px-1 py-1"><input value={r.address} onChange={(e) => updateCell(i, "address", e.target.value)} className="w-full px-1.5 py-1 border border-transparent hover:border-slate-200 rounded" /></td>
                      <td className="px-1 py-1"><input value={r.phone} onChange={(e) => updateCell(i, "phone", e.target.value)} className="w-full px-1.5 py-1 border border-transparent hover:border-slate-200 rounded" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">Les <strong>doublons</strong> (déjà dans votre liste) sont décochés automatiquement. Cochez-les si c'est une vraie nouvelle demande.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          {rows && <Button data-testid="import-confirm" onClick={confirm} disabled={busy || selectedCount === 0} className="bg-blue-600 hover:bg-blue-700">Importer {selectedCount} client(s)</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Calendar settings ---------------- */
function CalendarSettings({ onClose }) {
  const [url, setUrl] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/calendar").then(({ data }) => {
      setUrl(data.ical_url || "");
      setInfo(data);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/calendar", { url: url.trim() });
      toast.success("Lien enregistré");
      onClose();
    } catch (err) {
      toast.error("Erreur à l'enregistrement");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="calendar-settings-dialog">
        <DialogHeader>
          <DialogTitle>Lien Google Agenda (lecture seule)</DialogTitle>
          <DialogDescription>
            Collez ici l'« Adresse secrète au format iCal » de votre agenda. L'app la lit uniquement — elle ne peut rien modifier.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow">Adresse secrète iCal</label>
            <Input data-testid="calendar-ical-url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://calendar.google.com/calendar/ical/.../basic.ics" className="mt-1 font-mono text-xs" />
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-3 leading-relaxed">
            <strong>Où la trouver :</strong> Google Agenda (sur ordinateur) → survolez votre agenda → ⋮ →
            <em> Paramètres et partage</em> → section <em>Intégrer l'agenda</em> → <strong>Adresse secrète au format iCal</strong> → copier.
            <br />Rappel : ajoutez le <strong>code</strong> du client (ex. V-4821) dans le titre de l'événement pour qu'il se relie à la bonne fiche.
          </div>
          {info?.last_sync_at && (
            <p className="text-xs text-slate-400" data-testid="calendar-last-sync">
              Dernière synchro : {new Date(info.last_sync_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}
              {info.last_sync_result ? ` · ${info.last_sync_result.updated} RDV mis à jour` : ""}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button data-testid="calendar-save" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
